import { useMemo, useState } from "react";
import type { TimelineEntry } from "@/lib/activityTimeline/types";
import { formatRelativeOrAbsolute } from "@/lib/activityTimeline/formatRelativeTime";
import { timelineEntryHasSnapshots } from "@/lib/activityTimeline/auditDiffDisplay";
import type { TimelineDisplayScope } from "@/lib/activityTimeline/tier2Redaction";
import { copyTimelineStableId, navigateActivityDeepLink } from "@/lib/activityTimeline/deepLinkNavigation";
import { activityTimelineRowIsClickable } from "./activityTimelineRowUtils";
import {
  ActivityTimelineDiffPanel,
  ActivityTimelineMetadataPanel,
} from "./ActivityTimelineDiffPanel";

export type ActivityTimelineRowVariant = "default" | "forensics";

type Props = {
  entry: TimelineEntry;
  scope: TimelineDisplayScope;
  variant?: ActivityTimelineRowVariant;
  onDrillEntity?: (entityId: string, entityType: string) => void;
  drillActive?: boolean;
};

export function ActivityTimelineRow({
  entry,
  scope,
  variant = "default",
  onDrillEntity,
  drillActive = false,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [copyHint, setCopyHint] = useState<string | null>(null);
  const when = useMemo(() => formatRelativeOrAbsolute(entry.occurred_at), [entry.occurred_at]);
  const clickable = activityTimelineRowIsClickable(entry);
  const hasSnapshots = timelineEntryHasSnapshots(entry.before, entry.after);
  const showForensics = variant === "forensics" || scope === "platform";

  async function onCopyId() {
    const ok = await copyTimelineStableId(entry.id);
    setCopyHint(ok ? "Copied id" : "Copy failed");
    window.setTimeout(() => setCopyHint(null), 1600);
  }

  function handleActivate() {
    if (!clickable || !entry.deep_link) return;
    navigateActivityDeepLink(entry.deep_link);
  }

  const metaLine = (
    <div className="break-all text-[11px] text-gray-500 dark:text-zinc-400">
      <span>{entry.actor_label}</span>
      <span className="mx-1">·</span>
      <span className="font-mono">{entry.domain}</span>
      <span className="mx-1">·</span>
      <span className="font-mono">{entry.entity_type}</span>
      <span className="mx-1">·</span>
      <span className="font-mono">{entry.action}</span>
      {showForensics ? (
        <>
          <span className="mx-1">·</span>
          <span className="font-mono text-gray-400">{entry.source}</span>
        </>
      ) : null}
    </div>
  );

  const header = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 break-words text-sm font-medium text-gray-900 dark:text-zinc-100">{entry.summary}</span>
        <span className="shrink-0 text-[11px] text-gray-500 dark:text-zinc-400">{when}</span>
      </div>
      {metaLine}
    </>
  );

  const actions = (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {hasSnapshots || showForensics ? (
        <button
          type="button"
          className="text-xs text-gray-600 hover:underline dark:text-zinc-300"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Hide diff" : hasSnapshots ? "Show diff" : "Show details"}
        </button>
      ) : null}
      {onDrillEntity && entry.entity_id ? (
        <button
          type="button"
          className={
            drillActive
              ? "text-xs font-medium text-indigo-700 dark:text-indigo-300"
              : "text-xs text-indigo-600 hover:underline"
          }
          onClick={() => onDrillEntity(entry.entity_id, entry.entity_type)}
        >
          {drillActive ? "Drilling this entity" : "All events for this record"}
        </button>
      ) : null}
      {showForensics ? (
        <button
          type="button"
          className="text-xs text-indigo-600 hover:underline"
          onClick={() => void onCopyId()}
        >
          Copy id
        </button>
      ) : null}
      {entry.deep_link ? (
        <button
          type="button"
          className="text-xs text-indigo-600 hover:underline"
          onClick={() => navigateActivityDeepLink(entry.deep_link)}
        >
          Open record
        </button>
      ) : null}
      {copyHint ? <span className="text-[10px] text-gray-500">{copyHint}</span> : null}
    </div>
  );

  const expandedPanel =
    expanded && (hasSnapshots || showForensics) ? (
      <div className="mt-2 space-y-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-200">
        {showForensics ? (
          <div className="font-mono text-[10px] text-gray-500 dark:text-zinc-400">{entry.id}</div>
        ) : null}
        {hasSnapshots ? (
          <div>
            <div className="mb-1 font-medium text-gray-500 dark:text-zinc-400">Changes</div>
            <ActivityTimelineDiffPanel before={entry.before} after={entry.after} scope={scope} />
          </div>
        ) : null}
        {showForensics || Object.keys(entry.metadata).length > 0 ? (
          <div>
            <div className="mb-1 font-medium text-gray-500 dark:text-zinc-400">Metadata</div>
            <ActivityTimelineMetadataPanel metadata={entry.metadata} scope={scope} />
          </div>
        ) : null}
        {showForensics && entry.deep_link ? (
          <div>
            <span className="font-medium text-gray-500">deep_link:</span>{" "}
            <code className="text-[11px]">{entry.deep_link}</code>
          </div>
        ) : null}
      </div>
    ) : null;

  if (clickable) {
    return (
      <li className="py-2">
        <button
          type="button"
          className="w-full rounded-lg px-2 -mx-2 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/60"
          onClick={handleActivate}
        >
          {header}
        </button>
        {actions}
        {expandedPanel}
      </li>
    );
  }

  return (
    <li className="py-2">
      {header}
      {actions}
      {expandedPanel}
    </li>
  );
}
